import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Mail, Phone, CalendarDays, Loader2, ChevronLeft, ChevronRight, Inbox, Briefcase, GraduationCap, Building2, User as UserIcon, X, ChevronRight as GoIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { getExternalFeed } from '../api';
import { User, STAGE_LABELS, CandidateStage, DISPLAY_STAGE_GROUPS } from '../types';

/** 外部渠道识别：与后端 EXTERNAL_CHANNEL_SQL 保持一致 */
const CHANNELS = {
  website: { label: '官网投递', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
  referral: { label: '内推', cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  boss: { label: 'Boss直聘', cls: 'bg-orange-50 text-orange-600 border-orange-200' },
  email: { label: '邮箱投递', cls: 'bg-violet-50 text-violet-600 border-violet-200' },
  other: { label: '外部平台', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
} as const;
type ChannelKey = keyof typeof CHANNELS;

function channelOf(source?: string): ChannelKey {
  const s = source || '';
  if (s === '官网投递' || s === 'career_portal') return 'website';
  if (s === 'referral' || s.includes('内推')) return 'referral';
  if (s.includes('Boss')) return 'boss';
  if (s === 'email' || s.includes('邮箱')) return 'email';
  return 'other';
}

/** 阶段分栏配色（与人才池 CandidateCards 一致，另加 all 用于「全部」） */
const stageDotColors: Record<string, string> = {
  all: 'bg-primary-500',
  new: 'bg-gray-400', screening: 'bg-primary-300', screening_passed: 'bg-primary-400', screening_rejected: 'bg-gray-400',
  first_interview: 'bg-primary-400', first_interview_rejected: 'bg-gray-400',
  second_interview: 'bg-primary-500', second_interview_rejected: 'bg-gray-500',
  final_interview: 'bg-primary-500', final_interview_rejected: 'bg-gray-500',
  offer: 'bg-primary-600', offer_rejected: 'bg-gray-500', pending_onboard: 'bg-primary-600', onboarded: 'bg-primary-700',
};
const stageActiveColors: Record<string, string> = {
  all: 'text-primary-700 bg-primary-50/80',
  new: 'text-gray-800 bg-gray-100/80', screening: 'text-primary-700 bg-primary-50/80', screening_passed: 'text-primary-700 bg-primary-50/80', screening_rejected: 'text-gray-600 bg-gray-100/80',
  first_interview: 'text-primary-700 bg-primary-50/80', first_interview_rejected: 'text-gray-600 bg-gray-100/80',
  second_interview: 'text-primary-700 bg-primary-50/80', second_interview_rejected: 'text-gray-600 bg-gray-100/80',
  final_interview: 'text-primary-700 bg-primary-50/80', final_interview_rejected: 'text-gray-600 bg-gray-100/80',
  offer: 'text-primary-700 bg-primary-50/80', offer_rejected: 'text-gray-600 bg-gray-100/80', pending_onboard: 'text-primary-700 bg-primary-50/80', onboarded: 'text-primary-700 bg-primary-50/80',
};
const stageCountColors: Record<string, string> = {
  all: 'text-primary-600',
  new: 'text-gray-600', screening: 'text-primary-600', screening_passed: 'text-primary-600', screening_rejected: 'text-gray-500',
  first_interview: 'text-primary-600', first_interview_rejected: 'text-gray-500',
  second_interview: 'text-primary-600', second_interview_rejected: 'text-gray-500',
  final_interview: 'text-primary-600', final_interview_rejected: 'text-gray-500',
  offer: 'text-primary-600', offer_rejected: 'text-gray-500', pending_onboard: 'text-primary-600', onboarded: 'text-primary-600',
};

interface FeedTag { id: string; name: string; color: string; }
interface FeedItem {
  id: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  stage: string;
  position_id: string | null;
  position_title?: string | null;
  latest_company?: string | null;
  latest_title?: string | null;
  education?: string | null;
  school?: string | null;
  age?: number;
  work_years?: number;
  work_experiences?: { company?: string; title?: string }[];
  tags?: FeedTag[];
  created_at: string;
}

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
}

export default function WebsiteApplicants({ notificationBell, onOpenCandidate }: Props) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [scopeTotal, setScopeTotal] = useState(0);
  const [channelCounts, setChannelCounts] = useState<Record<string, number>>({});
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [channel, setChannel] = useState<ChannelKey | ''>('');
  const [activeGroupKey, setActiveGroupKey] = useState<string>('all');
  const [activeSubStage, setActiveSubStage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // 当前阶段分组 → 传给后端的 stages 列表（空=全部）
  const activeGroup = DISPLAY_STAGE_GROUPS.find(g => g.key === activeGroupKey);
  const stagesParam = activeGroupKey === 'all'
    ? ''
    : (activeSubStage ? activeSubStage : (activeGroup?.stages || []).join(','));

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getExternalFeed({ page, pageSize, search, channel, stages: stagesParam });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setScopeTotal(data.scopeTotal || 0);
      setChannelCounts(data.channelCounts || {});
      setStageCounts(data.stageCounts || {});
    } catch (err: any) {
      toast.error(err.response?.data?.message || '获取外部投递失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, channel, stagesParam]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const clearSearch = () => {
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const switchGroup = (key: string) => {
    setActiveGroupKey(key);
    const g = DISPLAY_STAGE_GROUPS.find(gg => gg.key === key);
    setActiveSubStage(g?.subCategories?.[0]?.stage ?? null);
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return iso; }
  };

  const groupCount = (g: typeof DISPLAY_STAGE_GROUPS[number]) =>
    g.stages.reduce((s, st) => s + (stageCounts[st] || 0), 0);

  const channelOptions: { key: ChannelKey | ''; label: string; count: number }[] = [
    { key: '', label: '全部渠道', count: Object.values(channelCounts).reduce((a, b) => a + (b || 0), 0) },
    ...(Object.keys(CHANNELS) as ChannelKey[]).map(k => ({ key: k as ChannelKey | '', label: CHANNELS[k].label, count: channelCounts[k] || 0 })),
  ];

  return (
    <div className="h-full flex flex-col gap-2.5">
      {/* Header card：标题 + 搜索 + 阶段分栏（与人才池一致） */}
      <div className="px-3 md:px-5 pt-3 pb-2 bg-white rounded-2xl border border-gray-200/80 shrink-0">
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <div className="shrink-0 pl-10 md:pl-0">
            <h1 className="text-lg font-bold text-gray-800">外部投递</h1>
            <p className="text-sm text-gray-400 mt-0.5">所有外部渠道投递进来的简历 · 共 {scopeTotal} 份</p>
          </div>

          {/* 搜索框 */}
          <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-[460px] mx-4 relative">
            <div className="relative flex items-center">
              <Search size={14} className="absolute left-3 text-gray-400 pointer-events-none" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="搜索候选人：姓名、邮箱或电话..."
                className="w-full h-9 pl-9 pr-20 rounded-xl border border-gray-200 bg-gray-50/80 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 focus:bg-white transition-all"
              />
              <div className="absolute right-1.5 flex items-center gap-1">
                {searchInput && (
                  <button type="button" onClick={clearSearch} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                    <X size={13} />
                  </button>
                )}
                <button type="submit" className="px-2.5 py-1 text-[11px] font-medium text-white rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors">
                  搜索
                </button>
              </div>
            </div>
          </form>

          {/* 渠道筛选 + 刷新 + 通知 */}
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={channel}
              onChange={e => { setChannel(e.target.value as ChannelKey | ''); setPage(1); }}
              className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {channelOptions.map(o => (
                <option key={o.key} value={o.key}>{o.label}（{o.count}）</option>
              ))}
            </select>
            <button
              onClick={fetchFeed}
              className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white hover:bg-gray-50 flex items-center gap-1.5 text-gray-600"
              title="刷新"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />刷新
            </button>
            {notificationBell}
          </div>
        </div>

        {/* 移动端搜索 */}
        <form onSubmit={handleSearch} className="md:hidden mb-3 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="搜索姓名、邮箱或电话..."
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          />
        </form>

        {/* 阶段分栏 */}
        <div className="flex items-stretch gap-1 pb-3 overflow-x-auto no-scrollbar">
          {/* 全部 */}
          <button
            onClick={() => switchGroup('all')}
            className={clsx(
              'flex-1 min-w-[56px] md:min-w-0 flex flex-col items-center justify-center gap-0.5 md:gap-1 py-2 md:py-2.5 rounded-xl transition-all duration-200 ease-out border',
              activeGroupKey === 'all'
                ? `${stageActiveColors.all} shadow-sm border-black/[0.04]`
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/60 border-transparent'
            )}
          >
            <div className="flex items-center gap-1">
              <span className={clsx('w-[5px] h-[5px] rounded-full hidden md:block', activeGroupKey === 'all' ? stageDotColors.all : 'bg-gray-300')} />
              <span className="text-[10px] md:text-[12px] font-medium whitespace-nowrap">全部</span>
            </div>
            <span className={clsx('text-sm md:text-base font-bold tabular-nums', activeGroupKey === 'all' ? stageCountColors.all : 'text-gray-300')}>{scopeTotal}</span>
          </button>
          {DISPLAY_STAGE_GROUPS.map(group => {
            const count = groupCount(group);
            const isActive = activeGroupKey === group.key;
            const primary = group.stages[0];
            return (
              <button
                key={group.key}
                onClick={() => switchGroup(group.key)}
                className={clsx(
                  'flex-1 min-w-[56px] md:min-w-0 flex flex-col items-center justify-center gap-0.5 md:gap-1 py-2 md:py-2.5 rounded-xl transition-all duration-200 ease-out border',
                  isActive
                    ? `${stageActiveColors[primary]} shadow-sm border-black/[0.04]`
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50/60 border-transparent'
                )}
              >
                <div className="flex items-center gap-1">
                  <span className={clsx('w-[5px] h-[5px] rounded-full hidden md:block', isActive ? stageDotColors[primary] : 'bg-gray-300')} />
                  <span className="text-[10px] md:text-[12px] font-medium whitespace-nowrap">{group.label}</span>
                </div>
                <span className={clsx('text-sm md:text-base font-bold tabular-nums', isActive ? stageCountColors[primary] : 'text-gray-300')}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* 子分类 */}
        {activeGroup?.subCategories && (
          <div className="flex items-center gap-2 pb-2 px-1 flex-wrap">
            {activeGroup.subCategories.map(sub => (
              <button
                key={sub.key}
                onClick={() => { setActiveSubStage(sub.stage); setPage(1); }}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  activeSubStage === sub.stage ? 'bg-gray-800 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {sub.label} ({stageCounts[sub.stage] || 0})
              </button>
            ))}
            <button
              onClick={() => { setActiveSubStage(null); setPage(1); }}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                activeSubStage === null ? 'bg-gray-800 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              全部 ({groupCount(activeGroup)})
            </button>
          </div>
        )}
      </div>

      {/* List — 卡片列表，风格与人才池一致 */}
      {loading ? (
        <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200/80 flex items-center justify-center text-gray-400">
          <Loader2 size={24} className="animate-spin mr-2" />加载中...
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200/80 flex flex-col items-center justify-center text-gray-400">
          <Inbox size={32} className="mb-2 opacity-40" />
          <p className="text-sm">{search || channel ? '没有匹配的外部投递简历' : '暂无外部投递简历'}</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-2 md:gap-2.5">
            {items.map((c) => {
              const ch = CHANNELS[channelOf(c.source)];
              const displayName = c.name || c.email?.split('@')[0] || '—';
              const infoParts: string[] = [];
              if (c.age && c.age > 0) infoParts.push(`${c.age}岁`);
              if (c.education) infoParts.push(c.education);
              if (c.work_years && c.work_years > 0) infoParts.push(`${c.work_years}年`);
              const exps = (c.work_experiences && c.work_experiences.length > 0)
                ? c.work_experiences.slice(0, 2)
                : (c.latest_company || c.latest_title)
                  ? [{ company: c.latest_company || undefined, title: c.latest_title || undefined }]
                  : [];
              return (
                <div
                  key={c.id}
                  onClick={() => onOpenCandidate && onOpenCandidate(c.id)}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm px-3 md:px-5 py-3 md:py-3.5 cursor-pointer transition-all duration-150 flex items-center hover:shadow-md hover:border-primary-200"
                >
                  <div className="flex gap-3 md:gap-4 items-center w-full">
                    {/* LEFT: 头像 + 姓名 + 阶段 + 渠道/标签 + 基本信息 */}
                    <div className="shrink-0 md:w-[240px] flex items-center gap-2.5 md:gap-3">
                      <div className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-gray-100 flex items-center justify-center">
                        <UserIcon size={20} className="text-gray-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-[13px] md:text-[14px] font-bold text-gray-900 truncate leading-5">{displayName}</h3>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 shrink-0">
                            {STAGE_LABELS[c.stage as CandidateStage] || c.stage || '—'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          <span className={clsx('px-1.5 py-0 rounded text-[10px] font-medium leading-4 border whitespace-nowrap', ch.cls)}>{ch.label}</span>
                          {c.tags && c.tags.slice(0, 2).map(t => (
                            <span key={t.id} className="px-1.5 py-0 rounded text-[10px] font-medium leading-4 whitespace-nowrap"
                              style={{ backgroundColor: t.color + '15', color: t.color, border: `1px solid ${t.color}30` }}>
                              {t.name}
                            </span>
                          ))}
                          {c.tags && c.tags.length > 2 && <span className="text-[10px] text-gray-400">+{c.tags.length - 2}</span>}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5 leading-4 truncate">{infoParts.length > 0 ? infoParts.join(' · ') : '\u00A0'}</p>
                      </div>
                    </div>

                    {/* CENTER: 工作经历 + 联系方式 - 移动端隐藏 */}
                    <div className="hidden md:block flex-1 min-w-0">
                      {exps.length > 0 ? (
                        <div className="space-y-1.5">
                          {exps.map((exp, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 min-w-0">
                              <Briefcase size={12} className={idx === 0 ? 'text-gray-500 shrink-0' : 'text-gray-300 shrink-0'} />
                              <p className={clsx('text-[12px] truncate leading-4', idx === 0 ? 'text-gray-800 font-medium' : 'text-gray-500')}>
                                {exp.company || ''}{exp.company && exp.title ? ' · ' : ''}{exp.title || ''}
                              </p>
                            </div>
                          ))}
                          <div className="flex items-center gap-3 text-[11px] text-gray-400 min-w-0">
                            {c.email && <span className="flex items-center gap-1 truncate"><Mail size={11} className="shrink-0" />{c.email}</span>}
                            {c.phone && <span className="flex items-center gap-1 shrink-0"><Phone size={11} />{c.phone}</span>}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5 text-gray-300"><Briefcase size={12} /><span className="text-[11px]">暂无工作经历</span></div>
                          <div className="flex items-center gap-3 text-[11px] text-gray-400 min-w-0">
                            {c.email && <span className="flex items-center gap-1 truncate"><Mail size={11} className="shrink-0" />{c.email}</span>}
                            {c.phone && <span className="flex items-center gap-1 shrink-0"><Phone size={11} />{c.phone}</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 学历 + 投递时间 - 移动端隐藏 */}
                    <div className="hidden md:block shrink-0 w-[180px]">
                      {c.school ? (
                        <div className="flex items-center gap-1.5">
                          <GraduationCap size={12} className="text-gray-400 shrink-0" />
                          <p className="text-[12px] text-gray-800 font-medium truncate leading-4">{c.school}{c.education ? ` · ${c.education}` : ''}</p>
                        </div>
                      ) : c.education ? (
                        <div className="flex items-center gap-1.5">
                          <GraduationCap size={12} className="text-gray-400 shrink-0" />
                          <span className="text-[12px] text-gray-600 leading-4">{c.education}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-gray-300">
                          <GraduationCap size={12} /><span className="text-[11px]">暂无学历</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <CalendarDays size={12} className="text-gray-400 shrink-0" />
                        <span className="text-[11px] text-gray-500 leading-4">{formatDate(c.created_at)}</span>
                      </div>
                    </div>

                    {/* RIGHT: 应聘岗位 + 查看 */}
                    <div className="shrink-0 w-[84px] md:w-[110px] flex flex-col items-end gap-1.5">
                      {c.position_title ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-primary-600 font-medium bg-primary-50 px-1.5 py-0.5 rounded-full truncate max-w-full">
                          <Building2 size={10} className="shrink-0" />{c.position_title}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded-full">未指定岗位</span>
                      )}
                      <span className="inline-flex items-center gap-0.5 text-xs text-primary-600 font-medium">
                        查看<GoIcon size={12} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-5 py-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">第 {page} / {totalPages} 页，共 {total} 条</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // 页码窗口：以当前页为中心最多 5 个，起点夹在 [1, totalPages-4]，避免首页重复出现多个 1
              const start = Math.min(Math.max(1, page - 2), Math.max(1, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={clsx('w-7 h-7 rounded-lg text-xs transition-colors', p === page ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-100')}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
